import * as React from 'react';

import {useMutation} from '@apollo/react-hooks';

import {Dialog} from 'shells/dialog/dialog';
import {SectionLabel} from 'shells/typography/section-label';

import type {
    SessionUser as SessionUserFragment,
    LeadStagesEdge as LeadStagesEdgeFragment,
    EmailAutomationStatusCancel as EmailAutomationStatusCancelMutation,
    EmailAutomationStatusCancelVariables as EmailAutomationStatusCancelMutationVariables,
    EmailAutomationStatusManualStart as EmailAutomationStatusManualStartMutation,
    EmailAutomationStatusManualStartVariables as EmailAutomationStatusManualStartMutationVariables,
    SendNowQueuedEmail as SendNowQueuedEmailMutation,
    SendNowQueuedEmailVariables as SendNowQueuedEmailMutationVariables,
} from 'nutshell-graphql-types';

import EMAIL_AUTOMATION_STATUS_CANCEL from '../../../email/automations/graphql/mutations/email-automation-status-cancel.graphql';
import EMAIL_AUTOMATION_STATUS_START from '../../../email/automations/graphql/mutations/email-automation-status-manual-start.graphql';
import SEND_NOW_QUEUED_EMAIL from '../../../email/mailbox/graphql/mutations/send-now-queued-email.graphql';

import {analyticsClient} from '../../../../analytics-client';

import {AutomatedEmailRow} from './automated-email-row';

import styles from './automated-emails-container.module.css';

type OwnProps = {
    currentLeadStageEdge: LeadStagesEdgeFragment;

    refetchLead: () => void;
    user: SessionUserFragment | null | undefined;
};

type GraphqlProps = {
    onEmailAutomationStatusCancel: (id: string) => Promise<any>;
    onEmailAutomationStatusStart: (id: string) => Promise<any>;
    onSendNextUnsentEmail: (id: string) => Promise<any>;
};

type Props = OwnProps & GraphqlProps;

const AutomatedEmailsContainerComponent = (props: Props) => {
    const [
        cancelLeadEmailSequenceStageSubscriptionId,
        setCancelLeadEmailSequenceStageSubscriptionId,
    ] = React.useState<string | null | undefined>(null);
    const [cancelButtonRef, setCancelButtonRef] = React.useState<HTMLElement | null | undefined>(
        null
    );

    return (
        <React.Fragment>
            <SectionLabel>Automated emails: {props.currentLeadStageEdge.node.name}</SectionLabel>
            <div className={styles['automated-email-cards']}>
                {/* If we have status rows, show those */}
                {props.currentLeadStageEdge.emailAutomationStatuses.map((status) => {
                    return (
                        <AutomatedEmailRow
                            key={status.id}
                            user={props.user}
                            emailAutomationStatus={status}
                            emailAutomation={status.emailAutomation}
                            onStart={props.onEmailAutomationStatusStart}
                            onCancel={setCancelLeadEmailSequenceStageSubscriptionId}
                            onSendNextUnsentEmail={props.onSendNextUnsentEmail}
                            setCancelButtonRef={setCancelButtonRef}
                        />
                    );
                })}
                {/* Also show all future automations without status rows */}
                {props.currentLeadStageEdge.unfiredEmailAutomations.map((emailAutomation) => {
                    return (
                        <AutomatedEmailRow
                            user={props.user}
                            key={emailAutomation.id}
                            emailAutomationStatus={null}
                            emailAutomation={emailAutomation}
                            onStart={props.onEmailAutomationStatusStart}
                            onCancel={setCancelLeadEmailSequenceStageSubscriptionId}
                            onSendNextUnsentEmail={props.onSendNextUnsentEmail}
                            setCancelButtonRef={setCancelButtonRef}
                        />
                    );
                })}
            </div>
            {cancelLeadEmailSequenceStageSubscriptionId ? (
                <Dialog
                    anchor={cancelButtonRef}
                    question='Are you sure you want to stop sending this email?'
                    primaryText='Yes, stop sending email'
                    secondaryText='No, keep sending'
                    isWarning={true}
                    onPrimary={() => {
                        props.onEmailAutomationStatusCancel(
                            cancelLeadEmailSequenceStageSubscriptionId
                        );
                        setCancelLeadEmailSequenceStageSubscriptionId(null);
                    }}
                    onSecondary={() => {
                        setCancelLeadEmailSequenceStageSubscriptionId(null);
                    }}
                />
            ) : undefined}
        </React.Fragment>
    );
};

export const AutomatedEmailsContainer = (props: OwnProps) => {
    const [emailAutomationStatusCancel] = useMutation<
        EmailAutomationStatusCancelMutation,
        EmailAutomationStatusCancelMutationVariables
    >(EMAIL_AUTOMATION_STATUS_CANCEL, {
        onError: () => {
            // this should not be reached :fingers_crossed:

            window.alert('Unable to cancel this sequence. Please contact support');
        },
    });

    const [emailAutomationStatusStart] = useMutation<
        EmailAutomationStatusManualStartMutation,
        EmailAutomationStatusManualStartMutationVariables
    >(EMAIL_AUTOMATION_STATUS_START, {
        onError: () => {
            // this should not be reached :fingers_crossed:

            window.alert('Unable to start this sequence. Please contact support');
        },
    });

    const [sendNow] = useMutation<SendNowQueuedEmailMutation, SendNowQueuedEmailMutationVariables>(
        SEND_NOW_QUEUED_EMAIL,
        {
            refetchQueries: ['GetEmailStats'],
        }
    );

    return (
        <AutomatedEmailsContainerComponent
            {...props}
            onEmailAutomationStatusCancel={(id: string) =>
                emailAutomationStatusCancel({
                    variables: {
                        input: {
                            emailAutomationStatus: {
                                id,
                            },
                        },
                    },
                }).then(() => {
                    analyticsClient.track(
                        analyticsClient.EVENTS
                            .TRACK_AUTOMATION_STATUS_MANUALLY_CANCELLED_FROM_LEAD_PAGE
                    );
                })
            }
            onEmailAutomationStatusStart={(id: string) =>
                emailAutomationStatusStart({
                    variables: {
                        input: {
                            id,
                        },
                    },
                }).then(() => {
                    props.refetchLead();

                    analyticsClient.track(
                        analyticsClient.EVENTS.TRACK_AUTOMATION_STATUS_STARTED_FROM_LEAD_PAGE
                    );
                })
            }
            onSendNextUnsentEmail={(nextQueuedEmailId: string) =>
                sendNow({
                    variables: {
                        payload: {
                            queuedEmail: {
                                id: nextQueuedEmailId,
                            },
                        },
                    },
                }).then(() => {
                    analyticsClient.track(
                        analyticsClient.EVENTS.TRACK_AUTOMATION_STATUS_SENT_NOW_FROM_LEAD_PAGE
                    );
                })
            }
        />
    );
};
